import { Component, Inject, OnInit, Renderer2, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DOCUMENT, CommonModule } from '@angular/common';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ThemeService, ColorPalette } from './services/theme.service';
import { AuthService } from './services/auth.service';
import dayjs from 'dayjs';
import 'dayjs/locale/nl';
import { UserService } from './services/user.service';
import { WebsocketService } from './services/websocket.service';
dayjs.locale('nl');
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { environment } from '../environments/environment';
import { filter } from 'rxjs';
import { StorageKey } from './enums/storageKey';
import { ToastService } from './services/toast.service';
import { NotificationType } from './enums/notificationType';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, MatSidenavModule, MatToolbarModule, CommonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  @ViewChild('drawer') drawer!: MatSidenav;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastService = inject(ToastService);

  environment: string = '';

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private themeService: ThemeService,
    public authService: AuthService,
    public userService: UserService,
    private websocketService: WebsocketService
  ) {
    if (Capacitor.isNativePlatform()) {
      document.body.classList.add('isNative');
      StatusBar.setOverlaysWebView({ overlay: true });

      document.addEventListener('DOMContentLoaded', () => {
        const insetTop =
          parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-top)')) || 40;
        document.documentElement.style.setProperty('--app-inset-top', insetTop + 'px');

        const insetBottom =
          parseInt(getComputedStyle(document.documentElement).getPropertyValue('env(safe-area-inset-bottom)')) || 40;
        document.documentElement.style.setProperty('--app-inset-bottom', insetBottom + 'px');
      });
    }

    // Apply initial theme
    this.themeService.paletteState$.subscribe(state => {
      this.applyTheme(state.branding, state.cta);
    });

    this.listenToDebugParam();
  }

  isImpersonating() {
    return this.authService.isImpersonating();
  }

  async ngOnInit() {
    this.environment = environment.env;

    if (Capacitor.isNativePlatform()) {
      await StatusBar.setStyle({ style: Style.Light });
    }

    this.authService.restoreSessionFromStorage();
    const me = this.userService.me;

    if (this.authService.isAuthenticated() && me) {
      this.themeService.updateBrandingColor(me.appStyle.idenitityColor);
      this.themeService.updateCtaColor(me.appStyle.primaryButtonColor);

      try {
        this.websocketService.connect();
      } catch (error) {
        console.error('WebSocket connection failed:', error);
      }
    }
  }

  applyTheme(primary: ColorPalette, accent: ColorPalette) {
    const style = this.renderer.createElement('style');
    const themeStyles = this.generateThemeVariables(primary, accent);

    this.renderer.appendChild(style, this.renderer.createText(themeStyles));
    this.renderer.appendChild(this.document.head, style);
  }

  private generateThemeVariables(primary: ColorPalette, accent: ColorPalette): string {
    const shades = ['10', '50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

    return `
      :root {${this.generatePaletteVariables(shades, primary, 'primary')}${this.generatePaletteVariables(
        shades,
        accent,
        'accent'
      )}
      }
    `;
  }

  generatePaletteVariables = (shades: string[], palette: ColorPalette, prefix: string) => {
    return shades
      .map(
        shade => `
      --${prefix}-${shade}: ${palette[shade].color};
      --${prefix}-contrast-${shade}: ${palette[shade].contrast};`
      )
      .join('');
  };

  private listenToDebugParam() {
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
      const debug = this.route.snapshot.queryParamMap.get('debug');

      if (debug === 'true') {
        localStorage.setItem(StorageKey.DebugMode, 'true');
        this.toastService.show('Debug mode', 'Debug mode is ingeschakeld.', NotificationType.Info);
      } else if (debug === 'false') {
        localStorage.removeItem(StorageKey.DebugMode);
        this.toastService.show('Debug mode', 'Debug mode is uitgeschakeld.', NotificationType.Info);
      } else {
        return;
      }

      this.router.navigate([], {
        queryParams: { debug: null },
        queryParamsHandling: 'merge'
      });
    });
  }
}
